import { http } from "@/providers/http";
import { appConfigService } from "@/providers/services/app/app-config";
import { commonService } from "@/providers/services/common-service";

/**
 * Ethics complaint API.
 *
 * The complaint form is public, so every call here goes out with the site's
 * anonymous token rather than a user session. The token is only fetched by the
 * home page layout, so a visitor who deep-links straight to /complaint may not
 * have one yet — ensureAnonymousToken() covers that case.
 */
export class ComplaintApi {
  private static INSTANCE = new ComplaintApi();

  static get instance() {
    return this.INSTANCE;
  }

  public async ensureAnonymousToken(): Promise<string> {
    const existing = localStorage.getItem("anonymousToken");
    if (existing) {
      return existing;
    }
    const data = await commonService.logInWithCredential(
      appConfigService.getClientId(),
      appConfigService.getClientKey(),
      "anonymous"
    );
    localStorage.setItem("anonymousToken", data.access_token);
    return data.access_token;
  }

  private async anonymousHeaders(contentType = "application/json") {
    const token = await this.ensureAnonymousToken();
    return {
      "Content-Type": contentType,
      Authorization: `Token ${token}`,
    };
  }

  /** Uploads one supporting document and returns its stored file details. */
  public async uploadFile(file: File) {
    const formdata = new FormData();
    formdata.append("file", file);
    const headers = await this.anonymousHeaders("multipart/form-data");
    const response = await http.post("upload", headers, formdata, true);
    return response.data;
  }

  /**
   * Files the complaint. `program` may be omitted, in which case the complaint
   * arrives unrouted and staff assign it to a program later.
   */
  public async submitComplaint(payload: any, program?: string) {
    const headers = await this.anonymousHeaders();
    // {appName} in the route only supplies a default program; `cadre` keeps it
    // unrouted, which is what the shared public form wants.
    const endpoint = `${program ? program.toLowerCase() : "cadre"}/complaint`;
    const response = await http.post(endpoint, headers, payload);
    return response.data;
  }
}

export const complaintApi = ComplaintApi.instance;
